import React, { useState, useMemo, useEffect } from "react";

/**
 * DataTable - Tabella riutilizzabile con ordinamento, filtri e paginazione
 *
 * Props:
 * - columns: Array di { key, label, sortable?, render?(row), width? }
 * - data: Array di oggetti
 * - loading: boolean
 * - emptyMessage: string
 * - onRowClick: (row) => void
 * - pageSize: number (default 10)
 * - pageSizeOptions: Array di numeri (default [10, 25, 50, 100])
 * - showPageSizeSelector: boolean (default true)
 * - onPageSizeChange: (newSize) => void - callback per cambio dimensione pagina
 * - searchable: boolean
 * - searchPlaceholder: string
 *
 * Server-side pagination props:
 * - serverSide: boolean - abilita paginazione server-side
 * - totalCount: number - totale record dal server
 * - currentPage: number - pagina corrente (controllata)
 * - onPageChange: (page) => void - callback cambio pagina
 * - onSearch: (searchTerm) => void - callback ricerca server-side
 * - onSort: (sortKey, sortDirection) => void - callback ordinamento server-side
 */
function DataTable({
  columns = [],
  data = [],
  loading = false,
  emptyMessage = "Nessun dato disponibile",
  onRowClick,
  pageSize: initialPageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  showPageSizeSelector = true,
  onPageSizeChange,
  searchable = true,
  searchPlaceholder = "Cerca...",
  className = "",
  // Server-side props
  serverSide = false,
  totalCount,
  currentPage: controlledPage,
  onPageChange,
  onSearch,
  onSort,
}) {
  // Client-side state
  const [internalPage, setInternalPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(initialPageSize);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Page size - usa valore interno o prop
  const pageSize = serverSide ? initialPageSize : internalPageSize;

  // Usa pagina controllata o interna
  const currentPage = serverSide ? controlledPage || 1 : internalPage;
  const setCurrentPage = serverSide ? onPageChange : setInternalPage;

  // Debounce ricerca
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      if (serverSide && onSearch) {
        onSearch(searchTerm);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, serverSide, onSearch]);

  // Client-side: Filtro ricerca
  const filteredData = useMemo(() => {
    if (serverSide) return data;
    if (!debouncedSearch) return data;
    const lowerSearch = debouncedSearch.toLowerCase();
    return data.filter((row) =>
      columns.some((col) => {
        const value = row[col.key];
        return value && String(value).toLowerCase().includes(lowerSearch);
      }),
    );
  }, [data, debouncedSearch, columns, serverSide]);

  // Client-side: Ordinamento
  const sortedData = useMemo(() => {
    if (serverSide) return filteredData;
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      const comparison = aVal < bVal ? -1 : 1;
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [filteredData, sortConfig, serverSide]);

  // Calcolo totale pagine
  const totalRecords = serverSide
    ? totalCount || data.length
    : sortedData.length;
  const totalPages = Math.ceil(totalRecords / pageSize);

  // Client-side: Paginazione
  const paginatedData = useMemo(() => {
    if (serverSide) return data; // Server gestisce la paginazione
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize, serverSide, data]);

  // Reset pagina quando cambiano filtri (solo client-side)
  useEffect(() => {
    if (!serverSide) {
      setInternalPage(1);
    }
  }, [debouncedSearch, sortConfig, serverSide]);

  const handleSort = (key) => {
    const newDirection =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction: newDirection });

    if (serverSide && onSort) {
      onSort(key, newDirection);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newSize) => {
    const size = parseInt(newSize);
    setInternalPageSize(size);
    setInternalPage(1); // Reset alla prima pagina
    if (onPageSizeChange) {
      onPageSizeChange(size);
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-300 ml-1">↕</span>;
    }
    return (
      <span className="ml-1">{sortConfig.direction === "asc" ? "↑" : "↓"}</span>
    );
  };

  // Calcola range record visualizzati
  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalRecords);

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      {/* Search bar */}
      {searchable && (
        <div className="p-4 border-b">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full md:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    col.sortable !== false
                      ? "cursor-pointer hover:bg-gray-100"
                      : ""
                  }`}
                  style={col.width ? { width: col.width } : {}}
                  onClick={() => col.sortable !== false && handleSort(col.key)}
                >
                  <span className="flex items-center">
                    {col.label}
                    {col.sortable !== false && <SortIcon columnKey={col.key} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5 text-primary-500"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    <span className="text-gray-500">Caricamento...</span>
                  </div>
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              paginatedData.map((row, rowIndex) => (
                <tr
                  key={row.id || rowIndex}
                  className={`${
                    onRowClick ? "cursor-pointer hover:bg-gray-50" : ""
                  } transition-colors`}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className="px-4 py-3 text-sm text-gray-900"
                    >
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-500">
              Mostrando {startRecord} - {endRecord} di {totalRecords}
            </div>
            {showPageSizeSelector && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-500">Righe:</label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Prima pagina"
            >
              «
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Pagina precedente"
            >
              ‹
            </button>

            {/* Numeri pagina */}
            {totalPages <= 7 ? (
              // Mostra tutte le pagine se <= 7
              [...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  onClick={() => handlePageChange(i + 1)}
                  className={`px-3 py-1 text-sm border rounded ${
                    currentPage === i + 1
                      ? "bg-primary-500 text-white border-primary-500"
                      : "hover:bg-gray-50"
                  }`}
                >
                  {i + 1}
                </button>
              ))
            ) : (
              // Mostra pagine con ellissi
              <>
                <button
                  onClick={() => handlePageChange(1)}
                  className={`px-3 py-1 text-sm border rounded ${
                    currentPage === 1
                      ? "bg-primary-500 text-white border-primary-500"
                      : "hover:bg-gray-50"
                  }`}
                >
                  1
                </button>

                {currentPage > 3 && (
                  <span className="px-2 py-1 text-gray-400">...</span>
                )}

                {[currentPage - 1, currentPage, currentPage + 1]
                  .filter((p) => p > 1 && p < totalPages)
                  .map((p) => (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p)}
                      className={`px-3 py-1 text-sm border rounded ${
                        currentPage === p
                          ? "bg-primary-500 text-white border-primary-500"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      {p}
                    </button>
                  ))}

                {currentPage < totalPages - 2 && (
                  <span className="px-2 py-1 text-gray-400">...</span>
                )}

                <button
                  onClick={() => handlePageChange(totalPages)}
                  className={`px-3 py-1 text-sm border rounded ${
                    currentPage === totalPages
                      ? "bg-primary-500 text-white border-primary-500"
                      : "hover:bg-gray-50"
                  }`}
                >
                  {totalPages}
                </button>
              </>
            )}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Pagina successiva"
            >
              ›
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Ultima pagina"
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* Mostra info anche con una sola pagina se ci sono dati */}
      {totalPages === 1 && totalRecords > 0 && (
        <div className="px-4 py-3 border-t flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-gray-500">
            Mostrando {totalRecords}{" "}
            {totalRecords === 1 ? "risultato" : "risultati"}
          </div>
          {showPageSizeSelector && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500">Righe:</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(e.target.value)}
                className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Mostra solo selettore se non ci sono dati ma vogliamo il controllo */}
      {totalRecords === 0 && showPageSizeSelector && !loading && (
        <div className="px-4 py-3 border-t flex items-center justify-end">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500">Righe:</label>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
